import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import styles from './Tooltip.module.scss';

interface TooltipProps {
  content: React.ReactNode;
  placement?: 'right' | 'right-start' | 'left' | 'bottom' | 'top' | 'bottom-start' | 'bottom-end' | 'top-start';
  hideArrow?: boolean;
  className?: string;
  tooltipClassName?: string;
  hoverStyles?: string;
  children: React.ReactNode;
  isFixed?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  className,
  tooltipClassName,
  hoverStyles,
  content,
  placement = 'top',
  hideArrow = false,
  isFixed = false,
}) => {
  const [isVisible, setVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const isMouseInsideTooltipRef = useRef(false);

  let hideTimeout;

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: isFixed ? 'fixed' : undefined,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 12],
        },
      },
    ],
  });

  // Show tooltip on mouse enter
  const handleMouseEnter = () => {
    setVisible(true);
    isMouseInsideTooltipRef.current = true;
    if (hideTimeout) {
      clearTimeout(hideTimeout); // Clear the previous timeout if the mouse re-enters
    }
  };

  // Hide tooltip with a delay on mouse leave
  const handleMouseLeave = () => {
    isMouseInsideTooltipRef.current = false;
    hideTimeout = setTimeout(() => {
      if (!isMouseInsideTooltipRef.current) {
        setVisible(false);
      }
    }, 150);
  };

  // Ensure tooltip hides if mouse moves out completely after timeout
  const handleTooltipMouseEnter = () => {
    isMouseInsideTooltipRef.current = true; // Prevent hiding when hovering directly over the tooltip
  };

  const handleTooltipMouseLeave = () => {
    isMouseInsideTooltipRef.current = false; // Tooltip mouse leave
    hideTimeout = setTimeout(() => {
      setVisible(false);
    }, 150);
  };

  return (
    <div
      ref={setReferenceElement}
      className={clsx(styles.tooltip, tooltipClassName)}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isVisible &&
        (isFixed ? (
          createPortal(
            <div
              ref={setPopperElement}
              className={clsx(
                styles.content,
                { [styles.hideArrow]: hideArrow, [styles.show]: isVisible },
                styles[placement],
                className,
              )}
              style={popperStyles.popper}
              {...attributes.popper}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
              data-testid="tooltip-content"
            >
              <div className={styles.text}>{content}</div>
            </div>,
            document.body,
          )
        ) : (
          <div
            ref={setPopperElement}
            className={clsx(
              styles.content,
              {
                [styles.hideArrow]: hideArrow,
                [styles.show]: isVisible,
                [styles.warning]: content.toString()?.startsWith('Warning'),
              },
              styles[placement],
              className,
            )}
            style={popperStyles.popper}
            {...attributes.popper}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
            data-testid="tooltip-content"
          >
            <div className={styles.text}>{content}</div>
            <div
              id="arrow"
              data-popper-arrow
            />
          </div>
        ))}

      <div className={clsx(styles.tooltipIcon, hoverStyles)}>{children}</div>
    </div>
  );
};
